import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import VisuallyHidden from '@atlaskit/visually-hidden';

import { MenuList } from '@atlassian/navigation-system/side-nav-items/menu-list';
import { LinkMenuItem } from '@atlassian/navigation-system/side-nav-items/link-menu-item';

import { AutomationOnboardingLoader } from '@confluence/manage-pages/entry-points/AutomationOnboardingLoader';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

const i18n = defineMessages({
	searchResult: {
		id: 'admin-side-navigation.search-result.number',
		defaultMessage:
			'{count, plural, =0 {No settings were found that match your search} one {{count} setting found} other {{count} settings found}}',
		description: 'The number of search results text for admin side navigation search input',
	},
});

export type PrimaryItemType = {
	title: string;
	href: string;
	isSelected: boolean;
	elemBefore: React.ReactNode;
	elemAfter?: React.ReactNode;
	onClick?: () => void;
};

export type PrimaryItemListProps = {
	primaryItems: PrimaryItemType[];
};

const PrimaryItem = ({
	title,
	href,
	isSelected,
	elemBefore,
	elemAfter,
	onClick,
}: PrimaryItemType) => (
	<LinkMenuItem
		isSelected={isSelected}
		href={href}
		elemBefore={elemBefore}
		elemAfter={elemAfter}
		onClick={onClick}
	>
		{title}
	</LinkMenuItem>
);

export const PrimaryItemList = ({ primaryItems }: PrimaryItemListProps) => {
	const { formatMessage } = useIntl();
	const { edition } = useSessionData();
	const isOnboardingEdition =
		edition === ConfluenceEdition.FREE || edition === ConfluenceEdition.STANDARD;

	return (
		<MenuList>
			<VisuallyHidden role="status">
				{formatMessage(i18n.searchResult, { count: primaryItems?.length })}
			</VisuallyHidden>
			{primaryItems.map((item, index) =>
				item.title === 'Automation' &&
				isOnboardingEdition &&
				fg('a4c_free_and_standard_onboarding') ? (
					<AutomationOnboardingLoader
						key={`${index}-${item.title}`}
						analyticsSubject="spaceSettingsNavigation"
						spotlightTarget={<PrimaryItem {...item} />}
					/>
				) : (
					<PrimaryItem key={`${index}-${item.title}`} {...item} />
				),
			)}
		</MenuList>
	);
};
